/**
 * B5 · এক্সপ্লোর — `/explore`
 * B6 · ক্ষেত্র বিস্তার — `/explore/:domain`
 * B7 · ক্যাটাগরি তালিকা — `/explore/:domain/:category`
 *
 * Frames: `Oprappo P0 Screens.dc.html`, "B5 · এক্সপ্লোর", "B6 · ক্ষেত্র বিস্তার",
 * "B7 · ক্যাটাগরি তালিকা".
 *
 * ── WIRED. THE CATALOGUE IS NO LONGER A FILE IN THE BUNDLE ──────────────────
 * Domains, subtitles, intent groups, categories, providers and visit fees all came from
 * `./domains.ts` and shipped to every phone. They now come from docs/05 § offerings'
 * three routes, through `./api.ts`:
 *
 *     B5  GET /v1/domains
 *     B6  GET /v1/domains/:id/intents
 *     B7  GET /v1/categories/:id/offerings
 *
 * What is still local is chrome — B5's title and placeholder, B7's ভিজিট ফি / সাজান /
 * ফিল্টার labels, and the glyph-key → drawn-mark table — because those are the same for
 * every person and are drawn rather than stored.
 *
 * Neither route is mounted in `apps/api` yet (see `./api.ts § none of the three is
 * mounted`). These screens therefore render their chrome and no rows against a live
 * Worker today, and the visual gate renders them against `fixtures/api/explore.*.json`,
 * whose values are transcribed from the frames. There is deliberately no bundled
 * fallback: a screen that silently shows design data when the API is thin is a screen
 * that looks reviewed and is not.
 *
 * B5's frame note: "Five live domains with a three-example subtitle each, then a tinted
 * আসছে divider and the seven still to come. The divider is what keeps the dimmed rows
 * from reading as broken." All twelve render; the artboard clips at seven rows because
 * it is 680px tall and cannot scroll, so the built list scrolls instead. That is the one
 * systematic departure from `overflow:hidden` in these frames, and it applies to every
 * scroll region in flows B and C.
 *
 * WHICH ROWS ARE LIVE IS THE SERVER'S CALL. `is_live` arrives on the row; the screen does
 * not compare an id against a list of five. CLAUDE.md § launch scope fixes P0 at five life
 * domains, and the day a sixth launches it launches in the database — a client-side list
 * would keep the new domain dimmed until somebody deployed the web app.
 *
 * B6's frame note: "Intent headings are verbs — নষ্ট জিনিস সারান, ঘর গুছিয়ে রাখুন. A user
 * scanning for a broken AC finds the group by what they want done, not by trade name."
 *
 * B7's frame note: "Visit fee is on the card, not discovered at checkout."
 *
 * An unknown `:domain` or `:category` renders `data-oprappo-not-found` rather than an
 * empty shell — `tools/visual-regression/capture-build.mjs` documents that marker, and a
 * shell that renders the app frame and diffs at 3% is a screen that looks reviewed and
 * is not. It is now reached when the SERVER has nothing for that id, which is the only
 * place that fact has ever really lived.
 */
import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Icon, IconButton, TabBar } from '@oprappo/ui';
import { formatTaka } from '@oprappo/contracts';
import { Frame, SAND_FRAME } from '../onboarding/frame.js';
import { Glyph } from '../onboarding/glyphs.js';
import { SurfaceStates } from '../../states/boundary.js';
import { useResource } from '../../data/resource.js';
import {
  fetchCategoryOfferings,
  fetchDomainIntents,
  fetchDomains,
  type CategoryOfferingsResponse,
  type DomainIntentsResponse,
  type DomainRow,
  type DomainsResponse,
} from './api.js';
import { CATEGORY_COPY, EXPLORE_COPY, markFor, type DomainMark } from './domains.js';

function NotFound(): JSX.Element {
  return <div data-oprappo-not-found="" />;
}

/* ---------------------------------------------------------- B5 · tab bar --- */

/**
 * The bar comes from `packages/ui` again.
 *
 * It used to be forked here. `TabBar` followed `Oprappo Design System.dc.html § 03`,
 * which draws a different bar from the one every P0 screen draws — a .14 shadow instead
 * of .1, per-cell padding instead of grid padding, a 22px icon instead of 21, #8B9795
 * instead of #5B6B69, and the icon in a flex box instead of inline. That last one is
 * worth 9px of line box, because an inline replaced element sits on the text baseline.
 *
 * Forking it here made B5 exact and left `/explore` with a bar 7px taller than every
 * other screen's in the running app — a real defect in the product, traded for a green
 * row. The fix belonged one level up, and `packages/ui/src/Nav.tsx` now transcribes the
 * frames rather than § 03. B5 stays at 0.00%, and B1, E1 and F1 improved as well.
 */

/**
 * The two mark sizes B5 draws — 20px in the list, 22px in the row header — as classes
 * for the empty slot below. `Icon` and `Glyph` take the number for their SVG `width`
 * and `height` ATTRIBUTES, which the CSP does not govern; the held-open slot was a
 * `style` attribute, which it does (`apps/web/public/_headers` § style-src).
 */
const GLYPH_BOX = { 20: 'w-[20px] h-[20px]', 22: 'w-[22px] h-[22px]' } as const;

function DomainGlyph({
  mark,
  size,
  stroke,
}: {
  mark: DomainMark;
  size: keyof typeof GLYPH_BOX;
  stroke: string;
}): JSX.Element {
  if (mark.from === 'icon') return <Icon name={mark.name} size={size} stroke={stroke} className="flex-none" />;
  if (mark.from === 'glyph') return <Glyph name={mark.name} size={size} stroke={stroke} className="flex-none" />;
  // No glyph exists in any design file for this domain. The slot is held so the names
  // stay aligned; nothing is drawn in it. See domains.ts § reported gaps.
  return <div className={`flex-none ${GLYPH_BOX[size]}`} aria-hidden />;
}

/* ------------------------------------------------------------ B5 · index --- */

/**
 * THE SCREEN IS A PURE FUNCTION OF THE PAYLOAD. The view renders what `GET /v1/domains`
 * returned; the container below does the loading. The split is what lets the fidelity
 * test render this screen from the same fixture the visual gate serves.
 */
export function ExploreView({ data }: { data: DomainsResponse | null }): JSX.Element {
  const navigate = useNavigate();
  const rows = data?.items ?? [];
  // The server decides which rows are live. The two lists below are the SAME list,
  // split at the আসছে band in the order the server sent it.
  const live = rows.filter((d) => d.is_live);
  const coming = rows.filter((d) => !d.is_live);

  return (
    <Frame className={SAND_FRAME}>
      <div className="px-[16px] pt-[16px] pb-[14px] border-b border-border">
        {/* 22px is the display band: leading 1.25, as drawn. */}
        <h1 className="m-0 font-anek font-semibold text-[22px] leading-[1.25] text-ink">
          {EXPLORE_COPY.title_bn}
        </h1>
        {/*
          `box-content py-0` — drawn as `height:44px;border:1px;padding:0 12px` with no
          `box-sizing`, so the field measures 46px outer and everything below it sits 2px
          lower than a border-box 44px would put it. `w-full` goes with it: under
          content-box a 100% width would push the border outside the 326px column, and a
          block-level flex button already fills the column on its own.
        */}
        <div className="flex mt-[12px]">
          <button
            type="button"
            onClick={() => navigate('/search')}
            className="flex-1 box-content border border-border bg-white h-[44px] flex items-center px-[12px] py-0 gap-[10px] text-left rounded-none"
          >
            <Icon name="search" size={16} stroke="#5B6B69" className="flex-none" />
            <span className="font-anek font-normal text-[15px] leading-[1.75] text-muted">
              {EXPLORE_COPY.search_placeholder_bn}
            </span>
          </button>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto">
        {live.map((d) => (
          <LiveRow key={d.id} domain={d} onOpen={() => navigate(`/explore/${d.id}`)} />
        ))}

        {/* `padding:14px 16px 11px; background:#EDEAE1` — the band that keeps the dimmed
            rows below it from reading as broken. No coming rows, no band: a divider over
            nothing is a promise of rows that are not there. */}
        {coming.length === 0 ? null : (
          <div className="px-[16px] pt-[14px] pb-[11px] bg-sand-deep border-b border-border">
            <div className="font-anek font-semibold text-[13px] leading-[1.75] text-muted tracking-[.03em]">
              {EXPLORE_COPY.coming_band_bn}
            </div>
          </div>
        )}

        {coming.map((d, i) => (
          <div
            key={d.id}
            className={
              'px-[16px] py-[12px] flex gap-[13px] items-center opacity-[.55]' +
              (i === coming.length - 1 ? '' : ' border-b border-border')
            }
          >
            <DomainGlyph mark={markFor(d.glyph)} size={20} stroke="#5B6B69" />
            <div className="flex-1 font-anek font-medium text-[15px] leading-[1.75] text-body">
              {d.name_bn}
            </div>
          </div>
        ))}
      </div>

      <TabBar
        activeKey="explore"
        onChange={(key) => {
          if (key === 'home') navigate('/');
          if (key === 'ai') navigate('/ai');
          if (key === 'activity') navigate('/activity');
          if (key === 'account') navigate('/account');
        }}
      />
    </Frame>
  );
}

export function ExploreScreen(): JSX.Element {
  const load = useCallback((signal: AbortSignal) => fetchDomains(signal), []);
  const { data, error } = useResource(load, 'domains');

  return (
    <SurfaceStates surface="discovery" error={error ?? undefined}>
      <ExploreView data={data} />
    </SurfaceStates>
  );
}

function LiveRow({ domain, onOpen }: { domain: DomainRow; onOpen: () => void }): JSX.Element {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="w-full px-[16px] py-[13px] border-b border-border flex gap-[13px] items-center text-left bg-transparent rounded-none"
    >
      <DomainGlyph mark={markFor(domain.glyph)} size={22} stroke="#0E3A45" />
      <span className="flex-1">
        <span className="block font-anek font-semibold text-[16px] leading-[1.75] text-ink">
          {domain.name_bn}
        </span>
        {domain.subtitle_bn === null || domain.subtitle_bn === '' ? null : (
          <span className="block font-anek font-normal text-[13px] leading-[1.75] text-muted mt-[1px]">
            {domain.subtitle_bn}
          </span>
        )}
      </span>
      <Glyph name="rowChevron" size={15} stroke="#5B6B69" className="flex-none" />
    </button>
  );
}

/* --------------------------------------------------------- B6 · by intent --- */

export function DomainView({ data }: { data: DomainIntentsResponse | null }): JSX.Element {
  const navigate = useNavigate();
  const groups = data?.groups ?? [];

  return (
    <Frame className={`${SAND_FRAME} p-[16px] overflow-y-auto`}>
      <div className="flex gap-[12px] items-center mb-[20px]">
        <IconButton control="back" size={20} onClick={() => navigate('/explore')} className="flex-none" />
        <h1 className="m-0 font-anek font-semibold text-[21px] leading-[1.75] text-ink">
          {data?.name_bn ?? ''}
        </h1>
      </div>

      {groups.map((g, i) => (
        <div key={g.id} className={i === groups.length - 1 ? '' : 'mb-[20px]'}>
          <div className="font-anek font-semibold text-[16px] leading-[1.75] text-teal mb-[11px]">
            {g.heading_bn}
          </div>
          <div className="grid grid-cols-2 gap-[9px]">
            {g.categories.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => navigate(`/explore/${data?.id ?? ''}/${c.id}`)}
                className="bg-white border border-border p-[12px] font-anek font-medium text-[15px] leading-[1.75] text-ink2 text-left rounded-none"
              >
                {c.label_bn}
              </button>
            ))}
          </div>
        </div>
      ))}
    </Frame>
  );
}

export function DomainScreen(): JSX.Element {
  const { domain } = useParams();
  const id = domain ?? '';
  const load = useCallback(
    (signal: AbortSignal) => fetchDomainIntents(id, signal),
    [id],
  );
  const { data, error, pending } = useResource(load, `domain:${id}`);

  // A domain the server does not have. The marker, not an empty shell — and only once
  // the read has finished, so a slow answer is not reported as a missing domain.
  if (id === '' || (!pending && data === null && error === null)) return <NotFound />;

  return (
    <SurfaceStates surface="discovery" error={error ?? undefined}>
      <DomainView data={data} />
    </SurfaceStates>
  );
}

/* ------------------------------------------------------ B7 · category list --- */

export function CategoryView({ data }: { data: CategoryOfferingsResponse | null }): JSX.Element {
  const navigate = useNavigate();
  const items = data?.items ?? [];

  return (
    <Frame className={SAND_FRAME}>
      <div className="px-[16px] pt-[16px]">
        <div className="flex gap-[12px] items-center mb-[13px]">
          <IconButton
            control="back"
            size={20}
            onClick={() => navigate(`/explore/${data?.domain_id ?? ''}`)}
            className="flex-none"
          />
          <h1 className="m-0 font-anek font-semibold text-[20px] leading-[1.75] text-ink">
            {data?.name_bn ?? ''}
          </h1>
        </div>
        <div className="flex justify-between items-center pb-[13px] border-b border-border">
          <div className="font-anek font-normal text-[14px] leading-[1.75] text-muted">
            {data?.count_bn ?? ''}
          </div>
        </div>
      </div>

      <div className="flex-1 min-h-0 px-[16px] overflow-y-auto">
        {items.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => navigate(`/offering/${p.id}`)}
            className="w-full py-[14px] border-b border-border flex gap-[12px] text-left bg-transparent px-0 rounded-none"
          >
            {/*
              `56×56; background:#EDEAE1; border:1px dashed #C4BFB2` — the design's marked
              image slot. docs/01: photography is placeholder and every image slot is
              marked, not faked.
            */}
            <span className="box-content w-[56px] h-[56px] bg-sand-deep border border-dashed border-border-warm flex-none" />
            <span className="flex-1 min-w-0">
              <span className="block font-anek font-semibold text-[16px] leading-[1.75] text-ink">
                {p.name_bn}
              </span>
              <span className="block font-anek font-normal text-[13.5px] leading-[1.75] text-muted mt-[2px]">
                {p.meta_bn}
              </span>
              {/*
                The whole row, label included, only when there IS a visit fee. `formatTaka`
                rounds null to 0, so an unconditional row prints `ভিজিট ফি ৳ 0` on every
                listing that has none — a free visit the shop never offered, and the one
                thing the contract's own comment forbids in words. Nothing is what a card
                with no fee shows.
              */}
              {p.visit_fee_minor !== null && (
                <span className="flex justify-between items-baseline mt-[7px]">
                  <span className="font-anek font-normal text-[13.5px] leading-[1.75] text-body">
                    {CATEGORY_COPY.visit_fee_bn}
                  </span>
                  <span className="font-manrope font-semibold text-[15px] leading-[1] text-ink [font-variant-numeric:tabular-nums]">
                    {formatTaka(p.visit_fee_minor)}
                  </span>
                </span>
              )}
            </span>
          </button>
        ))}
      </div>

      {/* `shadow: 0 -2px 14px rgba(14,58,69,.1)` — docs/02's `shadow-bottombar`. */}
      <div className="bg-white border-t border-border shadow-bottombar px-[16px] py-[11px] flex gap-[10px]">
        <button
          type="button"
          className="flex-1 box-content border-[1.5px] border-teal h-[46px] p-0 flex items-center justify-center gap-[8px] font-anek font-semibold text-[15px] leading-[1.75] text-teal bg-transparent rounded-none"
        >
          <Glyph name="sort" size={16} stroke="#0E3A45" />
          {CATEGORY_COPY.sort_bn}
        </button>
        <button
          type="button"
          className="flex-1 box-content border-[1.5px] border-teal h-[46px] p-0 flex items-center justify-center gap-[8px] font-anek font-semibold text-[15px] leading-[1.75] text-teal bg-transparent rounded-none"
        >
          <Glyph name="filter" size={16} stroke="#0E3A45" />
          {CATEGORY_COPY.filter_bn}
        </button>
      </div>
    </Frame>
  );
}

export function CategoryScreen(): JSX.Element {
  const { category } = useParams();
  const id = category ?? '';
  const load = useCallback(
    (signal: AbortSignal) => fetchCategoryOfferings(id, signal),
    [id],
  );
  const { data, error, pending } = useResource(load, `category:${id}`);

  if (id === '' || (!pending && data === null && error === null)) return <NotFound />;

  return (
    <SurfaceStates surface="discovery" error={error ?? undefined}>
      <CategoryView data={data} />
    </SurfaceStates>
  );
}
